import React, { createContext, useContext, useEffect, useMemo } from "react";

import { client } from "@/config";
import { handsFreeEnabledForWorkspace } from "@/data/workspaceConfig";
import type { AccountWithWsMembershipAvailability, Workspace, WorkspaceMembership } from "@/db/types";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import UseTimeouts from "@/hooks/useTimeouts";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { getWorkspaces, justWorkspaceMembershipsQuery, myWorkspaceMembershipsQuery } from "@/models/repositories";
import { UserContext } from "@/models/StateProviders/userProvider";
import { TelemetryContext } from "@/models/TelemetryProvider";
import { cleanupStaleWorkspaceKeys } from "@/utils/localStorage";
import { mapForHasId, mapForHasWorkspaceMembershipId, workspaceAccountsWithStatusSelect } from "../caching";
import { MyAccountContext } from "./myAccountProvider";

type WorkspaceState = {
  currentWorkspaceId?: string;
  myCurrentWorkspaceMembership?: WorkspaceMembership;
  workspaceMemberships?: WorkspaceMembership[];
  isWorkspaceAdmin?: () => boolean;
  isWorkspaceLimitedMember?: () => boolean;
  workspaces?: Workspace[];
  accountMap?: Map<string, AccountWithWsMembershipAvailability>;
  workspaceMembershipMap?: Map<string, AccountWithWsMembershipAvailability>;
};

export const WorkspaceContext = createContext<WorkspaceState>({
  currentWorkspaceId: undefined,
  myCurrentWorkspaceMembership: undefined,
  workspaceMemberships: [],
  isWorkspaceAdmin: () => false,
  isWorkspaceLimitedMember: () => false,
  workspaces: [],
  accountMap: new Map(),
  workspaceMembershipMap: new Map(),
});
const LOCATION_ENABLED = true;

const WorkspaceProvider = ({ children }) => {
  const { setAttribute } = useContext(TelemetryContext);
  const { myAccountId } = React.useContext(MyAccountContext);
  const { workspaceId } = useContext(UserContext);
  const workspaces = (useReactiveSelectQuery(getWorkspaces, [], "WorkspaceProvider:getWorkspaces") ??
    []) as Workspace[];

  const sendDesktopHandsFree = false;

  /** Same id passed to myWorkspaceMembershipsQuery — aligns with context consumers and route when UserContext lags. */
  const currentWorkspaceId: string | undefined =
    (workspaceId as string | undefined) ?? (workspaces[0]?.id as string | undefined);

  const handsFreeAllowed = handsFreeEnabledForWorkspace(currentWorkspaceId);

  useEffect(() => {
    if (currentWorkspaceId) {
      cleanupStaleWorkspaceKeys(currentWorkspaceId);
    }
  }, [currentWorkspaceId]);

  const myCurrentWorkspaceMemberships =
    useReactiveSelectQuery(
      myWorkspaceMembershipsQuery,
      [{ accountId: myAccountId, currentWorkspaceId }],
      "WorkspaceProvider:myWorkspaceMembershipsQuery",
    ) ?? [];

  useEffect(() => {
    const urlContainsEmailParam = new URLSearchParams(window.location.search).get("email") !== null;
    if (urlContainsEmailParam) {
      const newUrl = new URL(window.location.href);
      const urlPath = newUrl?.href.replace(newUrl?.origin, "");
      window.location.assign(`storyboard://${urlPath}`);
    }
  }, []);

  const myCurrentWorkspaceMembership = myCurrentWorkspaceMemberships[0];

  const workspaceMemberships =
    useReactiveSelectQuery(
      justWorkspaceMembershipsQuery,
      [{ workspaceId: currentWorkspaceId }],
      "WorkspaceProvider:justWorkspaceMembershipsQuery",
    ) ?? [];

  const isWorkspaceAdmin = React.useCallback(() => {
    return myCurrentWorkspaceMembership?.role === "admin";
  }, [myCurrentWorkspaceMembership]);

  const isWorkspaceLimitedMember = React.useCallback(() => {
    return myCurrentWorkspaceMembership?.role === "limitedMember";
  }, [myCurrentWorkspaceMembership]);

  useEffect(() => {
    if (myCurrentWorkspaceMembership?.role) {
      setAttribute("workspaceRole", myCurrentWorkspaceMembership?.role);
    }
  }, [myCurrentWorkspaceMembership?.role]);

  const accountsWithStatus = (useReactiveSelectQuery(
    workspaceAccountsWithStatusSelect,
    [currentWorkspaceId],
    "WorkspaceProvider:workspaceAccountsWithStatusSelect",
  ) ?? []) as AccountWithWsMembershipAvailability[];

  const accountMap = useMemo(
    () => mapForHasId(accountsWithStatus as AccountWithWsMembershipAvailability[]),
    [accountsWithStatus],
  );

  const workspaceMembershipMap = useMemo(
    () => mapForHasWorkspaceMembershipId(accountsWithStatus as AccountWithWsMembershipAvailability[]),
    [accountsWithStatus],
  );

  const localHeartBeat = UseTimeouts(60);
  useEffect(() => {
    if (!handsFreeAllowed || !sendDesktopHandsFree) return;
    if (localHeartBeat?.refresh === false) {
      if (sendDesktopHandsFree && LOCATION_ENABLED) {
        const successCallback = (position: {
          coords: {
            latitude: number;
            longitude: number;
          };
        }) => {
          const timestamp = new Date().toISOString();
          const heartbeat = {
            timestamp,
          };
          client.heartbeat(currentWorkspaceId, heartbeat);
        };
        const errorCallback = () => {
          const timestamp = new Date().toISOString();
          const heartBeatNoGeo = {
            timestamp,
          };
          client.heartbeat(currentWorkspaceId, heartBeatNoGeo);
        };

        navigator.geolocation.getCurrentPosition(successCallback, errorCallback);
      } else if (sendDesktopHandsFree) {
        const timestamp = new Date().toISOString();
        const heartBeatNoGeo = {
          timestamp,
        };
        client.heartbeat(currentWorkspaceId, heartBeatNoGeo);
      }
    }
  }, [handsFreeAllowed, localHeartBeat?.refresh, sendDesktopHandsFree, currentWorkspaceId]);

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspaceId,
        myCurrentWorkspaceMembership,
        workspaceMemberships,
        workspaces,
        accountMap,
        workspaceMembershipMap,

        isWorkspaceAdmin,
        isWorkspaceLimitedMember,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceProvider;
